import React from 'react';
import { MapPin, Star, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LandmarkCard = ({ landmark }) => {
  const navigate = useNavigate();

  const handleShowOnMap = () => {
    // Navigate to dashboard with landmark coordinates
    navigate('/dashboard', { 
      state: { 
        highlightLandmark: {
          latitude: landmark.latitude,
          longitude: landmark.longitude,
          name: landmark.name
        }
      }
    });
  };
  const getTypeIcon = (type) => {
    switch (type) {
      case 'museum':
        return '🏛️';
      case 'park':
        return '🌳';
      case 'church':
        return '⛪';
      case 'tourist_attraction':
        return '🎯';
      case 'monument':
        return '🗿';
      default:
        return '📍';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'museum':
        return 'bg-purple-100 text-purple-800';
      case 'park':
        return 'bg-green-100 text-green-800';
      case 'church':
        return 'bg-blue-100 text-blue-800';
      case 'tourist_attraction':
        return 'bg-orange-100 text-orange-800';
      case 'monument':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-indigo-100 text-indigo-800';
    }
  };

  const formatDistance = (distance) => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)}m`;
    }
    return `${distance}km`;
  };

  const getPriceLevel = (level) => {
    if (!level) return null;
    return '€'.repeat(level);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* Image */}
      <div className="relative h-48 bg-gray-100">
        {landmark.photoUrl ? (
          <img
            src={landmark.photoUrl}
            alt={landmark.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`absolute inset-0 flex items-center justify-center text-6xl ${landmark.photoUrl ? 'hidden' : 'flex'}`}
        >
          {getTypeIcon(landmark.type)}
        </div>
        
        
        {/* Distance badge */}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1">
          <div className="flex items-center space-x-1 text-xs font-medium text-gray-700">
            <MapPin className="w-3 h-3" />
            <span>{formatDistance(landmark.distance)}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 text-left">
              {landmark.name}
            </h3>
            
            {/* Rating and reviews */}
            {landmark.rating > 0 && (
              <div className="flex items-center space-x-2 mt-1">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-medium text-gray-700">
                    {landmark.rating.toFixed(1)}
                  </span>
                </div>
                {landmark.userRatingsTotal > 0 && (
                  <span className="text-sm text-gray-500">
                    ({landmark.userRatingsTotal} reviews)
                  </span>
                )}
                {landmark.priceLevel && (
                  <span className="text-sm text-gray-500 ml-2">
                    {getPriceLevel(landmark.priceLevel)}
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
            <button
              onClick={handleShowOnMap}
              className="p-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
              title="Show on map"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default LandmarkCard;
